/**
 * Resolve city / town / village labels for directory shop pins (Nominatim reverse).
 * Coarse cell cache (~1 km) so nearby shops share one lookup.
 *
 * Cache policy (G2):
 * - Sticky null ONLY after a successful HTTP reverse with empty/missing locality
 *   address parts (definitive empty — `kind: 'ok', label: null`).
 * - Non-OK HTTP (403/429/etc.) and other retryable reverse failures must NOT
 *   permanently cache null (`kind: 'retryable'`).
 * - Network throws skip cache so a later pass can succeed.
 */

const NOMINATIM_REVERSE_URL = 'https://nominatim.openstreetmap.org/reverse';
/** Nominatim usage policy: max 1 request/second. */
const NOMINATIM_MIN_INTERVAL_MS = 1100;
/** ~1.1 km cells — enough for shared city labels. */
const GEO_CELL_DECIMALS = 2;
/** v2: invalidate any v1 session entries that may have sticky-null from HTTP failures. */
const SESSION_CACHE_KEY = 'rpapp.customer.directoryGeoPlace.v2';

export interface NominatimAddressParts {
  readonly city?: string;
  readonly town?: string;
  readonly village?: string;
  readonly municipality?: string;
  readonly hamlet?: string;
  readonly suburb?: string;
  readonly city_district?: string;
}

interface NominatimReverseResponse {
  readonly address?: NominatimAddressParts;
}

/** Successful reverse (HTTP OK) — label may be null when address has no locality. */
export type GeoPlaceReverseOk = {
  readonly kind: 'ok';
  readonly label: string | null;
};

/** Transient failure — must not poison the cell cache. */
export type GeoPlaceReverseRetryable = {
  readonly kind: 'retryable';
};

export type GeoPlaceReverseResult = GeoPlaceReverseOk | GeoPlaceReverseRetryable;

export type GeoPlaceReverseFetcher = (
  lat: number,
  lng: number,
  options?: { readonly acceptLanguage?: string | null },
) => Promise<GeoPlaceReverseResult>;

export interface ResolveGeoPlaceLabelOptions {
  /** BCP 47 language for Accept-Language (e.g. `cs`, `en`). */
  readonly acceptLanguage?: string | null;
}

type PlaceCache = Record<string, string | null>;

/** Discriminated outcome for hook retry (G2): sticky empty vs retryable null. */
export type GeoPlaceResolveResult =
  | { readonly kind: 'label'; readonly label: string }
  | { readonly kind: 'empty' }
  | { readonly kind: 'retryable' };

const memoryCache: PlaceCache = {};
const inflight = new Map<string, Promise<GeoPlaceResolveResult>>();
const waitQueue: Array<() => void> = [];
let lastRequestAt = 0;
let queueRunning = false;
let reverseFetcherOverride: GeoPlaceReverseFetcher | null = null;

function readSessionCache(): PlaceCache {
  if (typeof sessionStorage === 'undefined') {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (raw === null || raw.length === 0) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const out: PlaceCache = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (value === null || typeof value === 'string') {
        out[key] = value;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeSessionCache(cache: PlaceCache): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // quota / private mode — memory cache still works
  }
}

let sessionHydrated = false;

function ensureSessionHydrated(): void {
  if (sessionHydrated) {
    return;
  }
  sessionHydrated = true;
  Object.assign(memoryCache, readSessionCache());
}

/** Stable cell key for lat/lng (2 decimal places). */
export function geoPlaceCellKey(lat: number, lng: number): string {
  return `${lat.toFixed(GEO_CELL_DECIMALS)},${lng.toFixed(GEO_CELL_DECIMALS)}`;
}

/**
 * Coarse label from a Nominatim forward-search `display_name`.
 * Takes the first comma-separated segment (city / area / postal place).
 * Discovery location chrome only — not a shipping address.
 */
export function coarsePlaceLabelFromDisplayName(displayName: string): string {
  const trimmed = displayName.trim();
  if (trimmed.length === 0) {
    return trimmed;
  }
  const first = trimmed.split(',')[0];
  if (first === undefined) {
    return trimmed;
  }
  const segment = first.trim();
  return segment.length > 0 ? segment : trimmed;
}

/**
 * Prefer city → town → village → municipality → hamlet → suburb.
 * Returns null when Nominatim has no usable locality.
 */
export function extractGeoPlaceLabel(
  address: NominatimAddressParts | null | undefined,
): string | null {
  if (address === null || address === undefined) {
    return null;
  }
  const candidates = [
    address.city,
    address.town,
    address.village,
    address.municipality,
    address.hamlet,
    address.suburb,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  }
  return null;
}

function rememberPlace(cacheKey: string, label: string | null): void {
  memoryCache[cacheKey] = label;
  writeSessionCache({ ...memoryCache });
}

/** Primary BCP-47 subtag for cache stability (`cs-CZ` → `cs`). */
function normalizeAcceptLanguage(explicit?: string | null): string | undefined {
  if (typeof explicit !== 'string') {
    return undefined;
  }
  const trimmed = explicit.trim().toLowerCase();
  if (trimmed.length === 0) {
    return undefined;
  }
  const primary = trimmed.split(/[-_]/)[0];
  return primary !== undefined && primary.length > 0 ? primary : trimmed;
}

function resolveAcceptLanguage(explicit?: string | null): string | undefined {
  const fromExplicit = normalizeAcceptLanguage(explicit);
  if (fromExplicit !== undefined) {
    return fromExplicit;
  }
  if (typeof document !== 'undefined') {
    return normalizeAcceptLanguage(document.documentElement.lang);
  }
  return undefined;
}

/** Cache key = cell + language so i18n switches do not serve wrong labels. */
function placeCacheKey(cellKey: string, acceptLanguage: string | undefined): string {
  return acceptLanguage !== undefined && acceptLanguage.length > 0
    ? `${cellKey}|${acceptLanguage}`
    : cellKey;
}

async function waitForRateLimit(): Promise<void> {
  const elapsed = Date.now() - lastRequestAt;
  if (elapsed >= NOMINATIM_MIN_INTERVAL_MS) {
    return;
  }
  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, NOMINATIM_MIN_INTERVAL_MS - elapsed);
  });
}

async function pumpQueue(): Promise<void> {
  if (queueRunning) {
    return;
  }
  queueRunning = true;
  try {
    while (waitQueue.length > 0) {
      const next = waitQueue.shift();
      if (next === undefined) {
        break;
      }
      await waitForRateLimit();
      next();
    }
  } finally {
    queueRunning = false;
  }
}

/**
 * Shared Nominatim rate-limit queue (≤1 req/s).
 * Used by reverse geo + map-view place search (G-NOMINATIM) — do not bypass / fetch off-queue.
 */
export function enqueueNominatim<T>(run: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    waitQueue.push(() => {
      lastRequestAt = Date.now();
      void run().then(resolve, reject);
    });
    void pumpQueue();
  });
}

/**
 * Browser-safe Nominatim reverse (G4):
 * - No User-Agent (browsers strip / forbid it; can hurt).
 * - Accept: application/json
 * - Accept-Language when available (param or document.documentElement.lang)
 */
export const defaultNominatimReverseFetcher: GeoPlaceReverseFetcher = async (
  lat,
  lng,
  options,
) => {
  const url = new URL(NOMINATIM_REVERSE_URL);
  url.searchParams.set('format', 'json');
  url.searchParams.set('lat', String(lat));
  url.searchParams.set('lon', String(lng));
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('zoom', '10');

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  const acceptLanguage = resolveAcceptLanguage(options?.acceptLanguage);
  if (acceptLanguage !== undefined) {
    headers['Accept-Language'] = acceptLanguage;
    url.searchParams.set('accept-language', acceptLanguage);
  }

  let response: Response;
  try {
    response = await fetch(url.toString(), { headers });
  } catch {
    return { kind: 'retryable' };
  }

  if (!response.ok) {
    // 403/429/5xx — retryable; do not sticky-cache null
    return { kind: 'retryable' };
  }

  let body: NominatimReverseResponse;
  try {
    body = (await response.json()) as NominatimReverseResponse;
  } catch {
    return { kind: 'retryable' };
  }

  // Sticky null only here: HTTP OK but empty/missing locality parts
  return { kind: 'ok', label: extractGeoPlaceLabel(body.address) };
};

function activeReverseFetcher(): GeoPlaceReverseFetcher {
  return reverseFetcherOverride ?? defaultNominatimReverseFetcher;
}

/**
 * Resolve place label with sticky-empty vs retryable discrimination (G2).
 * Cached by coarse cell; rate-limited Nominatim reverse.
 */
export async function resolveGeoPlaceLabelResult(
  lat: number,
  lng: number,
  options?: ResolveGeoPlaceLabelOptions,
): Promise<GeoPlaceResolveResult> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { kind: 'empty' };
  }
  ensureSessionHydrated();
  const cellKey = geoPlaceCellKey(lat, lng);
  const acceptLanguage = resolveAcceptLanguage(options?.acceptLanguage);
  const cacheKey = placeCacheKey(cellKey, acceptLanguage);
  if (Object.prototype.hasOwnProperty.call(memoryCache, cacheKey)) {
    const cached = memoryCache[cacheKey] ?? null;
    if (typeof cached === 'string' && cached.length > 0) {
      return { kind: 'label', label: cached };
    }
    // Sticky null after successful reverse with no locality.
    return { kind: 'empty' };
  }
  const existing = inflight.get(cacheKey);
  if (existing !== undefined) {
    return existing;
  }
  const pending = enqueueNominatim(async (): Promise<GeoPlaceResolveResult> => {
    try {
      const result = await activeReverseFetcher()(lat, lng, {
        acceptLanguage,
      });
      if (result.kind === 'retryable') {
        // Do not poison cache — allow a later resolve to succeed.
        return { kind: 'retryable' };
      }
      // Sticky null only for definitive empty locality after successful reverse.
      rememberPlace(cacheKey, result.label);
      if (typeof result.label === 'string' && result.label.length > 0) {
        return { kind: 'label', label: result.label };
      }
      return { kind: 'empty' };
    } catch {
      // Do not poison cache on network blips / unexpected throws.
      return { kind: 'retryable' };
    } finally {
      inflight.delete(cacheKey);
    }
  });
  inflight.set(cacheKey, pending);
  return pending;
}

/**
 * Resolve a city/village/place label for WGS84 coords.
 * Optional acceptLanguage for Accept-Language (else document lang when present).
 */
export async function resolveGeoPlaceLabel(
  lat: number,
  lng: number,
  options?: ResolveGeoPlaceLabelOptions,
): Promise<string | null> {
  const result = await resolveGeoPlaceLabelResult(lat, lng, options);
  return result.kind === 'label' ? result.label : null;
}

/** Test helper — inject reverse port (null restores default Nominatim). */
export function setDirectoryGeoPlaceReverseForTests(
  fetcher: GeoPlaceReverseFetcher | null,
): void {
  reverseFetcherOverride = fetcher;
}

/** Test helper — clears in-memory + session caches. */
export function resetDirectoryGeoPlaceCacheForTests(): void {
  for (const key of Object.keys(memoryCache)) {
    delete memoryCache[key];
  }
  inflight.clear();
  waitQueue.length = 0;
  lastRequestAt = 0;
  queueRunning = false;
  sessionHydrated = false;
  reverseFetcherOverride = null;
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(SESSION_CACHE_KEY);
    } catch {
      // ignore
    }
  }
}
